import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './DistributionBar.scss';

/**
 * Segment type for the distribution bar
 */
type Segment = {
  key: string;
  percentage: number; // 0-100
  color: string; // hex color
  label?: string; // optional label for legend/tooltip
};

/**
 * Props for DistributionBar component
 */
type DistributionBarProps = {
  /** Array of segments with custom colors (preferred) */
  segments?: Segment[];
  /** Show percentage labels inside each segment */
  showLabelsInside?: boolean;
  /** Show percentage labels above at segment END (right edge) */
  showLabelsAbove?: boolean;
  /** Show dashed divider lines between segments (default: true) */
  showDividers?: boolean;
  /** Show legend below the bar */
  showLegend?: boolean;
  /** Bar height variant - 'normal' = 24px, 'small' = 8px */
  size?: 'normal' | 'small';
  /** Additional CSS class */
  className?: string;
  // Legacy props for backward compatibility
  /** @deprecated Use segments prop instead */
  masteredPercentage?: number;
  /** @deprecated Use segments prop instead */
  inProgressPercentage?: number;
  /** @deprecated Use segments prop instead */
  notStartedPercentage?: number;
};

/**
 * DistributionBar - A reusable segmented progress bar
 * Supports custom segments with custom colors, or legacy KC mastery props
 */
function DistributionBar({
  segments,
  showLabelsInside = false,
  showLabelsAbove = false,
  showDividers = true,
  showLegend = false,
  size = 'normal',
  className = '',
  // Legacy props
  masteredPercentage,
  inProgressPercentage,
  notStartedPercentage,
}: DistributionBarProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  // Convert legacy props to segments if segments not provided
  const normalizedSegments = useMemo(() => {
    if (segments && segments.length > 0) {
      return segments;
    }

    // Backward compatibility: convert legacy props to segments
    if (
      masteredPercentage !== undefined ||
      inProgressPercentage !== undefined ||
      notStartedPercentage !== undefined
    ) {
      return [
        {
          key: 'mastered',
          percentage: masteredPercentage || 0,
          label: t('kc_mastered_full', { defaultValue: 'Contenidos dominados' }),
          color: '#0095A2',
        },
        {
          key: 'in-progress',
          percentage: inProgressPercentage || 0,
          label: t('kc_in_progress'),
          color: '#00B4C5',
        },
        {
          key: 'not-started',
          percentage: notStartedPercentage || 0,
          label: t('kc_not_started_full', { defaultValue: 'Contenidos sin empezar' }),
          color: '#D4FBFF',
        },
      ];
    }

    return [];
  }, [segments, masteredPercentage, inProgressPercentage, notStartedPercentage, t]);

  // Apply minimum width to very small segments for display
  const MIN_SEGMENT_WIDTH = 5; // 5% minimum width

  const adjustedSegments = useMemo(() => {
    const adjusted = normalizedSegments.map((segment) => ({
      ...segment,
      displayPercentage: Math.max(
        segment.percentage,
        segment.percentage > 0 ? MIN_SEGMENT_WIDTH : 0
      ),
    }));

    // Normalize to ensure total is 100%
    const totalDisplay = adjusted.reduce((sum, seg) => sum + seg.displayPercentage, 0);
    if (totalDisplay > 100) {
      const scale = 100 / totalDisplay;
      adjusted.forEach((seg) => {
        seg.displayPercentage *= scale;
      });
    }

    return adjusted;
  }, [normalizedSegments]);

  // Calculate cumulative positions for labels
  const cumulativePositions = useMemo(() => {
    const positions: Array<{
      key: string;
      percentage: number;
      displayPercentage: number;
      color: string;
      label?: string;
      start: number;
      end: number;
    }> = [];

    let cumulative = 0;
    adjustedSegments.forEach((segment) => {
      positions.push({
        ...segment,
        start: cumulative,
        end: cumulative + segment.displayPercentage,
      });
      cumulative += segment.displayPercentage;
    });

    return positions;
  }, [adjustedSegments]);

  // Filter out 0% segments for rendering
  const visibleSegments = cumulativePositions.filter((pos) => pos.percentage > 0);

  return (
    <div className={`distribution-bar distribution-bar--${size} ${className}`}>
      <div
        className={`distribution-bar__container ${showLabelsAbove ? 'distribution-bar__container--with-labels-above' : ''}`}
        role="progressbar"
        aria-label={t('analytics_kc_distribution')}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="distribution-bar__segments-wrapper">
          {visibleSegments.map((position, index) => (
            <div
              key={position.key}
              className={`distribution-bar__segment-wrapper ${showLabelsAbove && showDividers ? 'distribution-bar__segment-wrapper--with-border' : ''}`}
              style={{
                flex: position.displayPercentage,
                minWidth: position.displayPercentage > 0 ? '40px' : '0',
              }}
            >
              {showLabelsAbove && (
                <div
                  className={`distribution-bar__label-container ${
                    index === visibleSegments.length - 1 ? 'distribution-bar__label-container--last' : ''
                  }`}
                >
                  <span className="distribution-bar__percentage-value">
                    {position.percentage.toFixed(0)}%
                  </span>
                </div>
              )}

              <div
                className={`distribution-bar__segment ${
                  index === 0 ? 'distribution-bar__segment--first' : ''
                } ${index === visibleSegments.length - 1 ? 'distribution-bar__segment--last' : ''} ${
                  visibleSegments.length === 1 ? 'distribution-bar__segment--only' : ''
                }`}
                style={{ backgroundColor: position.color }}
                title={`${position.label || position.key}: ${position.percentage.toFixed(1)}%`}
              >
                {showLabelsInside && position.percentage > 0 && (
                  <span className="distribution-bar__segment-label">
                    {position.percentage.toFixed(0)}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showLegend && (
        <div className="distribution-bar__legend">
          {normalizedSegments.map((segment) => (
            <div key={segment.key} className="distribution-bar__legend-item">
              <span
                className="distribution-bar__legend-color"
                style={{ backgroundColor: segment.color }}
              />
              <span className="distribution-bar__legend-text">{segment.label || segment.key}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DistributionBar;
